// src/components/common/taskbox/OnboardingProgressWidget.js
import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Clock, AlertCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../hooks/useAuth';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useEmployees } from '../../../contexts/EmployeeContext';

const OnboardingProgressWidget = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const { onboardingData, fetchOnboardingData } = useOnboarding();
    const { employees } = useEmployees();

    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(null);

    // Find current employee
    const currentEmployee = employees.find(emp => emp.email === user?.email) || user;
    const employeeId = currentEmployee?.id;

    useEffect(() => {
        loadOnboardingProgress();
        // Refresh every 30 seconds for real-time updates
        const interval = setInterval(() => {
            if (fetchOnboardingData) {
                fetchOnboardingData(true); // Force refresh
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [employeeId, onboardingData, fetchOnboardingData]);

    const loadOnboardingProgress = () => {
        try {
            if (!employeeId || !onboardingData) {
                setLoading(false);
                return;
            }

            const employeeOnboarding = onboardingData[employeeId];

            if (!employeeOnboarding) {
                setProgress(null);
                setLoading(false);
                return;
            }

            // Calculate progress
            const steps = [
                {
                    name: 'Personal Information',
                    completed: !!employeeOnboarding.personalInfoCompleted,
                    status: employeeOnboarding.personalInfoCompleted ? 'completed' : 'pending'
                },
                {
                    name: 'Employment Details',
                    completed: !!employeeOnboarding.employmentDetailsCompleted,
                    status: employeeOnboarding.employmentDetailsCompleted ? 'completed' : 'pending'
                },
                {
                    name: 'Documents Upload',
                    completed: !!employeeOnboarding.documentsUploaded,
                    status: employeeOnboarding.documentsUploaded ? 'completed' : 'pending'
                },
                {
                    name: 'Bank Details',
                    completed: !!employeeOnboarding.bankDetailsCompleted,
                    status: employeeOnboarding.bankDetailsCompleted ? 'completed' : 'pending'
                },
                {
                    name: 'Admin Review',
                    completed: !!employeeOnboarding.adminReviewed,
                    status: employeeOnboarding.adminReviewed
                        ? 'completed'
                        : (employeeOnboarding.documentsUploaded ? 'in_review' : 'pending')
                }
            ];

            const completedSteps = steps.filter(s => s.completed).length;
            const totalSteps = steps.length;
            const percentComplete = Math.round((completedSteps / totalSteps) * 100);

            setProgress({
                steps,
                completedSteps,
                totalSteps,
                percentComplete,
                isFullyOnboarded: employeeOnboarding.adminReviewed === true
            });

            setLoading(false);
        } catch (error) {
            console.error('Error loading onboarding progress:', error);
            setLoading(false);
        }
    };

    // Don't show widget if fully onboarded
    if (!loading && (!progress || progress.isFullyOnboarded)) {
        return null;
    }

    const getStatusIcon = (step) => {
        if (step.completed) {
            return <CheckCircle className="w-5 h-5 text-green-500" />;
        } else if (step.status === 'in_review') {
            return <Clock className="w-5 h-5 text-yellow-500" />;
        } else {
            return <Circle className="w-5 h-5 text-gray-400" />;
        }
    };

    if (loading) {
        return (
            <div className={`rounded-xl p-6 border h-full ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-xl p-6 border h-full ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h3 className={`text-lg font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Onboarding Progress
                    </h3>
                    <p className={`text-sm mt-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Complete your profile setup
                    </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    progress?.percentComplete === 100
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}>
                    {progress?.percentComplete}%
                </div>
            </div>

            {/* Progress Bar */}
            <div className={`w-full h-2 rounded-full overflow-hidden mb-6 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
                <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                    style={{ width: `${progress?.percentComplete || 0}%` }}
                />
            </div>

            {/* Steps */}
            <div className="space-y-3">
                {progress?.steps.map((step, index) => (
                    <div
                        key={index}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            step.completed
                                ? theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'
                                : step.status === 'in_review'
                                    ? theme === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50'
                                    : theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                    >
                        {getStatusIcon(step)}
                        <div className="flex-1">
                            <p className={`text-sm font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {step.name}
                            </p>
                            {step.status === 'in_review' && (
                                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
                                    Under review
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Alert if not completed */}
            {progress && progress.percentComplete < 100 && (
                <div className={`mt-4 p-3 rounded-lg flex items-start gap-2 ${
                    theme === 'dark' ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
                }`}>
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-700'
                    }`}>
                        Complete all steps to access full portal features
                    </p>
                </div>
            )}
        </div>
    );
};

export default OnboardingProgressWidget;
